import { Conversation } from '@11labs/client';
import SiriWave from 'siriwave';

const startButton = document.getElementById('startButton');
const stopButton = document.getElementById('stopButton');
const connectionStatus = document.getElementById('connectionStatus');
const agentStatus = document.getElementById('agentStatus');
const siriContainer = document.querySelector('.siri-container');
const agentSelect = document.getElementById('agentSelect');
const noSleepVideo = document.getElementById('noSleepVideo');
const wakeLockStatus = document.getElementById('wakeLockStatus');
const microphoneSelect = document.getElementById('microphoneSelect');
const speakerSelect = document.getElementById('speakerSelect');

// Selected audio devices
let selectedMicrophoneId = localStorage.getItem('preferredMicrophoneId') || 'default';
let selectedSpeakerId = localStorage.getItem('preferredSpeakerId') || 'default';

// Function to enumerate and populate audio devices
async function populateAudioDevices() {
    try {
        // Request permission to access media devices
        await navigator.mediaDevices.getUserMedia({ audio: true });
        
        // Get list of all media devices
        const devices = await navigator.mediaDevices.enumerateDevices();
        
        // Clear existing options except the default
        while (microphoneSelect.options.length > 1) {
            microphoneSelect.remove(1);
        }
        
        while (speakerSelect.options.length > 1) {
            speakerSelect.remove(1);
        }
        
        // Filter and add audio input devices (microphones)
        const microphones = devices.filter(device => device.kind === 'audioinput');
        microphones.forEach(mic => {
            const option = document.createElement('option');
            option.value = mic.deviceId;
            option.text = mic.label || `Microphone ${microphoneSelect.options.length}`;
            microphoneSelect.appendChild(option);
            
            // Select the previously saved microphone if it exists
            if (mic.deviceId === selectedMicrophoneId) {
                option.selected = true;
            }
        });
        
        // Filter and add audio output devices (speakers)
        const speakers = devices.filter(device => device.kind === 'audiooutput');
        speakers.forEach(speaker => {
            const option = document.createElement('option');
            option.value = speaker.deviceId;
            option.text = speaker.label || `Speaker ${speakerSelect.options.length}`;
            speakerSelect.appendChild(option);
            
            // Select the previously saved speaker if it exists
            if (speaker.deviceId === selectedSpeakerId) {
                option.selected = true;
            }
        });
        
        console.log('Audio devices populated:', { microphones, speakers });
        
        // Check if saved devices are still available
        const micExists = microphones.some(mic => mic.deviceId === selectedMicrophoneId);
        const speakerExists = speakers.some(speaker => speaker.deviceId === selectedSpeakerId);
        
        if (!micExists) {
            selectedMicrophoneId = 'default';
            microphoneSelect.value = 'default';
        }
        
        if (!speakerExists) {
            selectedSpeakerId = 'default';
            speakerSelect.value = 'default';
        }
    } catch (error) {
        console.error('Error enumerating audio devices:', error);
        updateConnectionStatus('error', 'Error accessing audio devices');
    }
}

// Event listeners for device selection
microphoneSelect.addEventListener('change', function() {
    selectedMicrophoneId = this.value;
    console.log('Selected microphone:', selectedMicrophoneId);
    
    // Save preference to localStorage
    localStorage.setItem('preferredMicrophoneId', selectedMicrophoneId);
});

speakerSelect.addEventListener('change', function() {
    selectedSpeakerId = this.value;
    console.log('Selected speaker:', selectedSpeakerId);
    
    // Save preference to localStorage
    localStorage.setItem('preferredSpeakerId', selectedSpeakerId);
    
    // If browser supports setSinkId, set the audio output device
    if (typeof HTMLMediaElement.prototype.setSinkId === 'function') {
        // Note: This will affect any audio elements created after this point
        // For existing audio elements, you would need to call setSinkId on each one
        console.log('Audio output device set to:', selectedSpeakerId);
    } else {
        console.warn('setSinkId is not supported in this browser. Speaker selection may not work.');
    }
});

// Populate devices when the page loads
document.addEventListener('DOMContentLoaded', () => {
    populateAudioDevices();
    
    // Check browser compatibility for audio output selection
    if (typeof HTMLMediaElement.prototype.setSinkId !== 'function') {
        // Add a warning message to the speaker select
        const warningDiv = document.createElement('div');
        warningDiv.className = 'text-yellow-600 text-xs mt-1';
        warningDiv.innerHTML = '<i class="fas fa-exclamation-triangle mr-1"></i> Your browser may not support speaker selection';
        
        // Insert after the speaker select
        const speakerParent = speakerSelect.parentNode;
        speakerParent.insertBefore(warningDiv, speakerSelect.nextSibling);
    }
    
    // Setup audio settings modal
    setupAudioSettingsModal();
});

// Function to setup the audio settings modal
function setupAudioSettingsModal() {
    const audioSettingsButton = document.getElementById('audioSettingsButton');
    const audioSettingsModal = document.getElementById('audioSettingsModal');
    const closeSettingsButton = document.getElementById('closeSettingsButton');
    const saveSettingsButton = document.getElementById('saveSettingsButton');
    const testMicButton = document.getElementById('testMicButton');
    const testSpeakerButton = document.getElementById('testSpeakerButton');
    const micLevelIndicator = document.getElementById('micLevelIndicator');
    
    // Variables for audio testing
    let micStream = null;
    let audioContext = null;
    let analyser = null;
    let dataArray = null;
    let testAudio = null;
    let micTesting = false;
    
    // Function to open the modal
    function openModal() {
        audioSettingsModal.classList.remove('hidden');
        // Add animation classes
        setTimeout(() => {
            audioSettingsModal.querySelector('.bg-white').classList.add('animate-fadeIn');
        }, 10);
        
        // Prevent scrolling on the body
        document.body.style.overflow = 'hidden';
    }
    
    // Function to close the modal
    function closeModal() {
        // Stop any ongoing tests
        stopMicrophoneTest();
        stopSpeakerTest();
        
        // Add animation classes for closing
        const modalContent = audioSettingsModal.querySelector('.bg-white');
        modalContent.classList.add('animate-fadeOut');
        
        // Wait for animation to complete before hiding
        setTimeout(() => {
            audioSettingsModal.classList.add('hidden');
            modalContent.classList.remove('animate-fadeIn', 'animate-fadeOut');
            // Restore scrolling
            document.body.style.overflow = '';
        }, 200);
    }
    
    // Function to test microphone
    async function testMicrophone() {
        if (micTesting) {
            stopMicrophoneTest();
            return;
        }
        
        try {
            // Update button state
            testMicButton.innerHTML = '<i class="fas fa-stop text-red-600"></i>';
            testMicButton.title = 'Stop Test';
            micTesting = true;
            
            // Get microphone stream
            const constraints = {
                audio: {
                    deviceId: selectedMicrophoneId !== 'default' ? { exact: selectedMicrophoneId } : undefined
                }
            };
            
            micStream = await navigator.mediaDevices.getUserMedia(constraints);
            
            // Create audio context
            audioContext = new (window.AudioContext || window.webkitAudioContext)();
            analyser = audioContext.createAnalyser();
            
            // Connect the microphone to the analyser
            const source = audioContext.createMediaStreamSource(micStream);
            source.connect(analyser);
            
            // Configure analyser
            analyser.fftSize = 256;
            const bufferLength = analyser.frequencyBinCount;
            dataArray = new Uint8Array(bufferLength);
            
            // Start visualization
            visualizeMic();
            
        } catch (error) {
            console.error('Error testing microphone:', error);
            stopMicrophoneTest();
            
            // Show error message
            const errorDiv = document.createElement('div');
            errorDiv.className = 'text-red-600 text-xs mt-1';
            errorDiv.innerHTML = `<i class="fas fa-exclamation-circle mr-1"></i> ${error.message}`;
            
            const parent = micLevelIndicator.parentNode;
            // Remove any existing error message
            const existingError = parent.querySelector('.text-red-600');
            if (existingError) {
                parent.removeChild(existingError);
            }
            
            parent.appendChild(errorDiv);
            
            // Remove error message after 5 seconds
            setTimeout(() => {
                if (errorDiv.parentNode === parent) {
                    parent.removeChild(errorDiv);
                }
            }, 5000);
        }
    }
    
    // Function to visualize microphone input
    function visualizeMic() {
        if (!micTesting) return;
        
        // Get volume data
        analyser.getByteFrequencyData(dataArray);
        
        // Calculate average volume
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
            sum += dataArray[i];
        }
        const average = sum / dataArray.length;
        
        // Update level indicator (0-100%)
        const level = Math.min(100, Math.max(0, average * 1.5)); // Scale for better visibility
        micLevelIndicator.querySelector('div').style.width = `${level}%`;
        
        // Continue visualization
        requestAnimationFrame(visualizeMic);
    }
    
    // Function to stop microphone test
    function stopMicrophoneTest() {
        // Reset button
        testMicButton.innerHTML = '<i class="fas fa-play text-gray-600"></i>';
        testMicButton.title = 'Test Microphone';
        micTesting = false;
        
        // Reset level indicator
        if (micLevelIndicator) {
            micLevelIndicator.querySelector('div').style.width = '0%';
        }
        
        // Stop and clean up audio resources
        if (micStream) {
            micStream.getTracks().forEach(track => track.stop());
            micStream = null;
        }
        
        if (audioContext) {
            if (audioContext.state !== 'closed') {
                audioContext.close();
            }
            audioContext = null;
            analyser = null;
            dataArray = null;
        }
    }
    
    // Function to test speaker
    function testSpeaker() {
        // Stop any existing test
        stopSpeakerTest();
        
        // Create test audio
        testAudio = new Audio('https://assets.mixkit.co/sfx/preview/mixkit-simple-countdown-922.mp3');
        
        // Set output device if supported
        if (typeof testAudio.setSinkId === 'function' && selectedSpeakerId !== 'default') {
            testAudio.setSinkId(selectedSpeakerId)
                .then(() => {
                    console.log('Audio output device set successfully');
                    playTestAudio();
                })
                .catch(err => {
                    console.error('Error setting audio output device:', err);
                    // Play anyway on default device
                    playTestAudio();
                });
        } else {
            // Play on default device
            playTestAudio();
        }
        
        function playTestAudio() {
            // Update button state
            testSpeakerButton.innerHTML = '<i class="fas fa-volume-up text-green-600 animate-pulse"></i>';
            testSpeakerButton.title = 'Playing...';
            testSpeakerButton.disabled = true;
            
            // Play test sound
            testAudio.play();
            
            // Reset button when done
            testAudio.onended = () => {
                testSpeakerButton.innerHTML = '<i class="fas fa-play text-gray-600"></i>';
                testSpeakerButton.title = 'Test Speaker';
                testSpeakerButton.disabled = false;
                testAudio = null;
            };
        }
    }
    
    // Function to stop speaker test
    function stopSpeakerTest() {
        if (testAudio) {
            testAudio.pause();
            testAudio.currentTime = 0;
            testAudio = null;
            
            // Reset button
            testSpeakerButton.innerHTML = '<i class="fas fa-play text-gray-600"></i>';
            testSpeakerButton.title = 'Test Speaker';
            testSpeakerButton.disabled = false;
        }
    }
    
    // Open modal when settings button is clicked
    if (audioSettingsButton) {
        audioSettingsButton.addEventListener('click', openModal);
    }
    
    // Close modal when close button is clicked
    if (closeSettingsButton) {
        closeSettingsButton.addEventListener('click', closeModal);
    }
    
    // Close modal when save button is clicked
    if (saveSettingsButton) {
        saveSettingsButton.addEventListener('click', closeModal);
    }
    
    // Test microphone when test button is clicked
    if (testMicButton) {
        testMicButton.addEventListener('click', testMicrophone);
    }
    
    // Test speaker when test button is clicked
    if (testSpeakerButton) {
        testSpeakerButton.addEventListener('click', testSpeaker);
    }
    
    // Close modal when clicking outside the content
    audioSettingsModal.addEventListener('click', (event) => {
        if (event.target === audioSettingsModal) {
            closeModal();
        }
    });
    
    // Close modal when ESC key is pressed
    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !audioSettingsModal.classList.contains('hidden')) {
            closeModal();
        }
    });
    
    // Add a small indicator next to the settings button to show selected devices
    function updateDeviceIndicator() {
        // Create or get the indicator element
        let indicator = document.getElementById('deviceIndicator');
        if (!indicator) {
            indicator = document.createElement('span');
            indicator.id = 'deviceIndicator';
            indicator.className = 'text-xs text-gray-500 ml-1';
            audioSettingsButton.appendChild(indicator);
        }
        
        // Show a dot if custom devices are selected
        if (selectedMicrophoneId !== 'default' || selectedSpeakerId !== 'default') {
            indicator.innerHTML = '<span class="inline-block w-2 h-2 bg-blue-500 rounded-full"></span>';
            indicator.title = 'Custom audio devices selected';
        } else {
            indicator.innerHTML = '';
            indicator.title = '';
        }
    }
    
    // Update indicator when devices change
    microphoneSelect.addEventListener('change', updateDeviceIndicator);
    speakerSelect.addEventListener('change', updateDeviceIndicator);
    
    // Initial update
    updateDeviceIndicator();
}

// Add event listener for the refresh devices button
const refreshDevicesButton = document.getElementById('refreshDevicesButton');
if (refreshDevicesButton) {
    refreshDevicesButton.addEventListener('click', async () => {
        // Show loading state
        refreshDevicesButton.innerHTML = '<i class="fas fa-spinner fa-spin mr-1"></i> Refreshing...';
        refreshDevicesButton.disabled = true;
        
        try {
            // Re-enumerate devices
            await populateAudioDevices();
            
            // Show success message temporarily
            refreshDevicesButton.innerHTML = '<i class="fas fa-check mr-1"></i> Updated!';
            refreshDevicesButton.classList.add('text-green-600');
            refreshDevicesButton.classList.remove('text-blue-600');
            
            // Reset button after a delay
            setTimeout(() => {
                refreshDevicesButton.innerHTML = '<i class="fas fa-sync-alt mr-1"></i> Refresh Devices';
                refreshDevicesButton.classList.remove('text-green-600');
                refreshDevicesButton.classList.add('text-blue-600');
                refreshDevicesButton.disabled = false;
            }, 2000);
        } catch (error) {
            console.error('Error refreshing devices:', error);
            
            // Show error message
            refreshDevicesButton.innerHTML = '<i class="fas fa-exclamation-circle mr-1"></i> Error!';
            refreshDevicesButton.classList.add('text-red-600');
            refreshDevicesButton.classList.remove('text-blue-600');
            
            // Reset button after a delay
            setTimeout(() => {
                refreshDevicesButton.innerHTML = '<i class="fas fa-sync-alt mr-1"></i> Refresh Devices';
                refreshDevicesButton.classList.remove('text-red-600');
                refreshDevicesButton.classList.add('text-blue-600');
                refreshDevicesButton.disabled = false;
            }, 2000);
        }
    });
}

// Listen for device changes (devices being added or removed)
navigator.mediaDevices.addEventListener('devicechange', async () => {
    console.log('Media devices changed - updating device lists');
    
    // Only update if we're not in an active conversation
    if (!conversation) {
        await populateAudioDevices();
        
        // Update the device indicator
        const updateDeviceIndicator = () => {
            const audioSettingsButton = document.getElementById('audioSettingsButton');
            if (!audioSettingsButton) return;
            
            let indicator = document.getElementById('deviceIndicator');
            if (!indicator) {
                indicator = document.createElement('span');
                indicator.id = 'deviceIndicator';
                indicator.className = 'text-xs text-gray-500 ml-1';
                audioSettingsButton.appendChild(indicator);
            }
            
            if (selectedMicrophoneId !== 'default' || selectedSpeakerId !== 'default') {
                indicator.innerHTML = '<span class="inline-block w-2 h-2 bg-blue-500 rounded-full"></span>';
                indicator.title = 'Custom audio devices selected';
            } else {
                indicator.innerHTML = '';
                indicator.title = '';
            }
        };
        
        updateDeviceIndicator();
        
        // Show a notification that devices have changed
        const audioSettingsButton = document.getElementById('audioSettingsButton');
        if (audioSettingsButton) {
            // Add a temporary notification
            const notification = document.createElement('div');
            notification.className = 'absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center animate-pulse';
            notification.innerHTML = '!';
            notification.title = 'Audio devices changed';
            audioSettingsButton.style.position = 'relative';
            audioSettingsButton.appendChild(notification);
            
            // Remove notification after 5 seconds
            setTimeout(() => {
                if (notification.parentNode === audioSettingsButton) {
                    audioSettingsButton.removeChild(notification);
                }
            }, 5000);
        }
    } else {
        console.log('Device change detected but not updating during active conversation');
        
        // Show a notification to the user that devices changed
        const connectionStatusText = connectionStatus.textContent;
        updateConnectionStatus('connecting', 'Audio devices changed! End call to use new devices');
        
        // Restore original status after 3 seconds
        setTimeout(() => {
            if (conversation) {
                updateConnectionStatus('connected', 'Connected');
            } else {
                updateConnectionStatus('disconnected', 'Disconnected');
            }
        }, 3000);
    }
});

// Screen Wake Lock variable
let wakeLock = null;
let noSleepEnabled = false;

// Function to update wake lock status indicator
function updateWakeLockStatus(isActive) {
    if (isActive) {
        wakeLockStatus.style.display = 'inline-flex';
        wakeLockStatus.classList.add('connected');
        wakeLockStatus.classList.remove('disconnected');
    } else {
        wakeLockStatus.style.display = 'none';
        wakeLockStatus.classList.remove('connected');
        wakeLockStatus.classList.add('disconnected');
    }
}

// Function to enable no-sleep fallback
function enableNoSleepFallback() {
    if (!noSleepEnabled) {
        // Create a silent video source
        if (!noSleepVideo.src) {
            noSleepVideo.src = 'data:video/mp4;base64,AAAAIGZ0eXBtcDQyAAAAAG1wNDJtcDQxaXNvbWF2YzEAAATKbW9vdgAAAGxtdmhkAAAAANLEP5XSxD+VAAB1MAAAdU4AAQAAAQAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAgAAACFpb2RzAAAAABCAgIAQAE////9//w6AgIAEAAAAAQAABDV0cmFrAAAAXHRraGQAAAAH0sQ/ldLEP5UAAAABAAAAAAAAdU4AAAAAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAQAAAAAFAAAAAtAAAAAABtmRpYQAAAABsbXZoZAAAAADSxD+V0sQ/lQAAdU4AAHVOAAAAAAQAAAAAAAEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAVaG1kaAAAAABadG9vAAAAMGZyYW1lAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAABAAAAAAA==';
        }
        
        // Play the video to prevent sleep
        noSleepVideo.play()
            .then(() => {
                console.log('NoSleep fallback enabled');
                noSleepEnabled = true;
                updateWakeLockStatus(true);
            })
            .catch(err => {
                console.error('NoSleep fallback error:', err);
            });
    }
}

// Function to disable no-sleep fallback
function disableNoSleepFallback() {
    if (noSleepEnabled) {
        noSleepVideo.pause();
        noSleepVideo.currentTime = 0;
        noSleepEnabled = false;
        console.log('NoSleep fallback disabled');
        updateWakeLockStatus(false);
    }
}

// Function to request wake lock
async function requestWakeLock() {
    try {
        if ('wakeLock' in navigator) {
            wakeLock = await navigator.wakeLock.request('screen');
            console.log('Wake Lock is active');
            updateWakeLockStatus(true);
            
            // Add event listener for visibility change to re-acquire wake lock if needed
            document.addEventListener('visibilitychange', handleVisibilityChange);
            
            // Add release event listener
            wakeLock.addEventListener('release', () => {
                console.log('Wake Lock was released');
                updateWakeLockStatus(false);
                wakeLock = null;
            });
        } else {
            console.warn('Wake Lock API not supported in this browser, using fallback');
            enableNoSleepFallback();
        }
    } catch (err) {
        console.error(`Error requesting wake lock: ${err.message}`);
        // Use fallback if Wake Lock API fails
        enableNoSleepFallback();
    }
}

// Function to release wake lock
function releaseWakeLock() {
    if (wakeLock) {
        wakeLock.release()
            .then(() => {
                console.log('Wake Lock has been released');
                wakeLock = null;
                updateWakeLockStatus(false);
                document.removeEventListener('visibilitychange', handleVisibilityChange);
            })
            .catch((err) => {
                console.error(`Error releasing wake lock: ${err.message}`);
            });
    }
    
    // Also disable the fallback
    disableNoSleepFallback();
}

// Handle visibility change (e.g., when user switches tabs)
function handleVisibilityChange() {
    if (document.visibilityState === 'visible' && conversation) {
        // Re-acquire wake lock when page becomes visible again and conversation is active
        requestWakeLock();
    } else if (document.visibilityState === 'hidden' && noSleepEnabled) {
        // When tab is hidden, we need to ensure the fallback is still working
        // Some browsers may pause video playback when tab is not visible
        disableNoSleepFallback();
        // We'll re-enable it when the tab becomes visible again
    }
}

// Initialize SiriWave
const siriWave = new SiriWave({
    container: siriContainer,
    width: siriContainer.clientWidth || 400,
    height: 200,
    style: 'ios9',
    amplitude: 0.2,
    speed: 0.1,
    frequency: 4,
    color: '#3B82F6',
    cover: true,
    autostart: false, // Start manually when needed
    pixelDepth: 0.02,
    lerpSpeed: 0.1
});

// Initially stop the animation
updateAnimation('inactive');

// Configuration
const config = {
    apiKey: import.meta.env.VITE_ELEVENLABS_API_KEY,
    agentIds: {
        agent1: import.meta.env.VITE_ELEVENLABS_AGENT_ID1,
        agent2: import.meta.env.VITE_ELEVENLABS_AGENT_ID2,
        agent3: import.meta.env.VITE_ELEVENLABS_AGENT_ID3,
        agent4: import.meta.env.VITE_ELEVENLABS_AGENT_ID4
    },
    debug: true, // Enable debug logging
};

// Validate configuration
if (!config.apiKey) {
    console.error('Missing API key. Please check .env.local file.');
    updateConnectionStatus('error', 'Error: Missing API key configuration');
    startButton.disabled = true;
}

function getSelectedAgentId() {
    const selectedAgent = agentSelect.value;
    return config.agentIds[selectedAgent];
}

function updateConnectionStatus(status, message) {
    // Remove all status classes
    connectionStatus.classList.remove('connected', 'disconnected', 'connecting', 'error');
    
    // Add appropriate class
    connectionStatus.classList.add(status);
    
    // Update text
    connectionStatus.innerHTML = `<i class="fas fa-circle mr-2 text-xs"></i> ${message}`;
}

function updateAnimation(mode) {
    switch (mode) {
        case 'speaking':
            siriWave.start();
            siriWave.setAmplitude(1.5);
            siriWave.setSpeed(0.25);
            break;
        case 'listening':
            siriWave.start();
            siriWave.setAmplitude(0.8);
            siriWave.setSpeed(0.15);
            break;
        case 'inactive':
            siriWave.stop();
            break;
    }
}

let conversation;

async function startConversation() {
    try {
        const selectedAgentId = getSelectedAgentId();
        if (!selectedAgentId) {
            updateConnectionStatus('error', 'Error: Selected agent not configured');
            return;
        }

        // Request microphone permission with selected device
        const audioConstraints = { 
            audio: {
                deviceId: selectedMicrophoneId !== 'default' ? { exact: selectedMicrophoneId } : undefined
            } 
        };
        
        // Log the audio constraints being used
        console.log('Using audio constraints:', audioConstraints);
        
        // Request permission with the selected microphone
        await navigator.mediaDevices.getUserMedia(audioConstraints);

        // Request wake lock to prevent screen from sleeping
        await requestWakeLock();

        // Update UI to show we're connecting
        updateConnectionStatus('connecting', 'Connecting...');
        updateAnimation('listening');
        
        // Start the conversation with selected devices
        conversation = await Conversation.startSession({
            agentId: selectedAgentId,
            apiKey: config.apiKey,
            
            // Add audio configuration with selected devices
            audioConfig: {
                inputDeviceId: selectedMicrophoneId !== 'default' ? selectedMicrophoneId : undefined,
                outputDeviceId: selectedSpeakerId !== 'default' ? selectedSpeakerId : undefined
            },
            
            onConnect: () => {
                updateConnectionStatus('connected', 'Connected');
                updateAnimation('listening');
                
                // If browser supports setSinkId and a specific speaker is selected,
                // try to set it for any audio elements created by the conversation
                if (typeof HTMLMediaElement.prototype.setSinkId === 'function' && 
                    selectedSpeakerId !== 'default') {
                    // Find any audio elements created by the conversation
                    // This is a generic approach - you may need to adjust based on the library's implementation
                    setTimeout(() => {
                        const audioElements = document.querySelectorAll('audio');
                        audioElements.forEach(audio => {
                            if (audio.setSinkId) {
                                audio.setSinkId(selectedSpeakerId)
                                    .then(() => console.log('Audio output device set successfully'))
                                    .catch(err => console.error('Error setting audio output device:', err));
                            }
                        });
                    }, 1000); // Give time for audio elements to be created
                }
            },
            onDisconnect: () => {
                updateConnectionStatus('disconnected', 'Disconnected');
                startButton.style.display = 'flex';
                stopButton.style.display = 'none';
                agentStatus.textContent = 'inactive';
                updateAnimation('inactive');
            },
            onError: (error) => {
                console.error('Error:', error);
                updateConnectionStatus('error', `Error: ${error.message}`);
                startButton.style.display = 'flex';
                stopButton.style.display = 'none';
                updateAnimation('inactive');
            },
            onModeChange: (mode) => {
                const newMode = mode.mode === 'speaking' ? 'speaking' : 'listening';
                agentStatus.textContent = newMode;
                updateAnimation(newMode);
                if (config.debug) {
                    console.log('Mode changed:', mode);
                }
            },
            onMessage: (message) => {
                if (config.debug) {
                    console.log('Message received:', message);
                }
            }
        });
    } catch (error) {
        console.error('Error starting conversation:', error);
        updateConnectionStatus('error', `Error: ${error.message}`);
        startButton.style.display = 'flex';
        stopButton.style.display = 'none';
        updateAnimation('inactive');
    }
}

async function stopConversation() {
    console.log('Stopping conversation...');
    return new Promise(async (resolve) => {
        // Release wake lock when conversation stops
        releaseWakeLock();
        
        if (conversation) {
            console.log('Conversation exists, attempting to disconnect');
            try {
                await conversation.disconnect();
                console.log('Disconnection successful');
            } catch (error) {
                console.error('Error stopping conversation:', error);
            } finally {
                // Always update UI and reset conversation, even if disconnect fails
                console.log('Resetting conversation and updating UI');
                conversation = null;
                updateConnectionStatus('disconnected', 'Disconnected');
                startButton.style.display = 'flex';
                stopButton.style.display = 'none';
                agentStatus.textContent = 'inactive';
                updateAnimation('inactive');
                resolve();
                
                // Refresh the page
                console.log('Refreshing page after call end');
                window.location.reload();
            }
        } else {
            console.log('No active conversation to disconnect');
            resolve();
            
            // Refresh the page even if there was no active conversation
            console.log('Refreshing page after call end');
            window.location.reload();
        }
    });
}

// Event Listeners
startButton.addEventListener('click', startConversation);
stopButton.addEventListener('click', stopConversation);

// Expose stopConversation to window for the back button
window.stopConversation = stopConversation;

// Make SiriWave responsive on window resize
window.addEventListener('resize', () => {
    // Only update if container exists
    if (siriContainer) {
        siriWave.width = siriContainer.clientWidth || 400;
    }
});
